import React, { useState } from "react";
import { Box, Button, Flex, Text, Tooltip, useClipboard, useColorModeValue } from "@chakra-ui/react";
import { FaCheck, FaChevronDown, FaExclamationTriangle, FaRegCopy } from "react-icons/fa";

/**
 * The calculations behind an answer, as worksheets under it: what was computed, from which inputs and where each
 * came from, step by step with the rule for each step, and what the code could not settle. Money is set as an
 * accountant's schedule (figures in one column, the total ruled), a rating as its rating string with each part
 * explained beneath and the rules as footnotes, anything else as a plain schedule of its steps. Ratings, money and deadlines are computed by code (api/calc), never by the
 * model, so the worksheet is the source of every computed figure in the answer; those figures are marked in the
 * answer text (markComputedFigures).
 */

/** One `calculate` tool result, as api/calc/tool.py stores it with the chat. */
export interface CalcResult {
    ok: true;
    kind: string;
    inputs: Record<string, any>;
    /** For each input, where the model found it: "the question", "assumed: ...". */
    sources_of_inputs?: Record<string, string>;
    headline: string;
    result?: Record<string, any>;
    steps: { label: string; value: string; rule?: string }[];
    warnings: string[];
    /** The law and tables the calculation applied. */
    sources?: string[];
}

const KIND: Record<string, string> = {
    pd_rating: "Permanent disability rating",
    pd_money: "Permanent disability payments",
    td_rate: "Temporary disability rate",
    combine: "Combined values",
    deadline: "Deadline",
};

const FIELD: Record<string, string> = {
    date_of_injury: "Date of injury", age: "Age at injury", birth_date: "Born", occupational_group: "Occupational group",
    average_weekly_earnings: "Earnings a week", rating: "Final rating", return_to_work: "Return to work", method: "Method",
    start: "From", end: "To", amount: "Period", unit: "Unit", service: "Service", values: "Values", board_rules: "Board rules",
};

/**
 * The calculations behind an answer: the `calculate` results between the user's question and the answer (`messages`
 * from the question onwards). Tool calls are stored as an assistant message of tool_use blocks, their results as a
 * user message of tool_result blocks. Occupation lookups are left out (they only feed a rating), a later run with
 * the same kind and inputs replaces an earlier one, and a run whose inputs another run of the same kind repeats and
 * adds to (PD money without, then with, return to work) is left out: the fuller worksheet already shows its figures.
 */
export function calcsForAnswer(messages: any[]): CalcResult[] {
    const calcIds = new Set<string>();
    const out = new Map<string, CalcResult>();
    for (const m of messages) {
        if (!Array.isArray(m?.content)) continue;
        for (const c of m.content) {
            if (c?.type === "tool_use" && c.name === "calculate") calcIds.add(c.id);
            if (c?.type === "tool_result" && calcIds.has(c.tool_use_id)) {
                try {
                    const parsed = JSON.parse(typeof c.content === "string" ? c.content : "");
                    if (parsed?.ok && parsed.kind !== "occupation") {
                        out.set(`${parsed.kind}:${JSON.stringify(parsed.inputs)}`, parsed as CalcResult);
                    }
                } catch {
                    // not a calculation result (or a truncated one): nothing to show
                }
            }
        }
    }
    const calcs = Array.from(out.values());
    return calcs.filter((a) => !calcs.some((b) => b !== a && b.kind === a.kind && extends_(b.inputs, a.inputs)));
}

const absent = (v: unknown) => v === undefined || v === null || v === "";

/** Whether `fuller` has every input of `base`, with the same values, and at least one more. */
function extends_(fuller: unknown, base: unknown): boolean {
    return within(base, fuller) && !within(fuller, base);
}

/** Every value present in `a` is present in `b` and the same (objects and arrays compared item by item). */
function within(a: unknown, b: unknown): boolean {
    if (absent(a)) return true;
    if (Array.isArray(a)) return Array.isArray(b) && a.length === b.length && a.every((x, i) => within(x, b[i]));
    if (a && typeof a === "object") {
        return !!b && typeof b === "object" && !Array.isArray(b)
            && Object.entries(a as Record<string, unknown>).every(([k, v]) => within(v, (b as Record<string, unknown>)[k]));
    }
    return String(a) === String(b);
}

export function usd(n: number): string {
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Every dollar figure the calculations produced, with the step that shows how it was reached. */
export function computedFigures(calcs: CalcResult[]): Map<number, string> {
    const out = new Map<number, string>();
    for (const calc of calcs) {
        const walk = (v: unknown) => {
            if (typeof v === "number" && v >= 1) {
                const cents = Math.round(v * 100);
                if (out.has(cents)) return;
                const shown = usd(v);
                const step = calc.steps.find((s) => s.value.includes(shown));
                out.set(cents, step ? `${step.label}: ${step.value}` : `${KIND[calc.kind] ?? "Calculation"}: ${calc.headline}`);
            } else if (Array.isArray(v)) v.forEach(walk);
            else if (v && typeof v === "object") Object.values(v).forEach(walk);
        };
        for (const [k, v] of Object.entries(calc.result ?? {})) {
            if (/rate|total|pension|two_thirds|max|min|paid/.test(k)) walk(v);
        }
    }
    return out;
}

/**
 * Wraps the dollar figures of an answer (already prepared as Markdown with inline HTML) that a calculation produced,
 * so they render as computed figures (see CalcFigure). Text inside HTML tags is left alone. "$290" matches $290.00.
 */
export function markComputedFigures(html: string, figures: Map<number, string>): string {
    if (figures.size === 0) return html;
    return html.split(/(<[^>]+>)/).map((part) => part.startsWith("<") ? part : part.replace(
        /\$\s?\d{1,3}(?:,\d{3})*(?:\.\d{2})?(?![\d,])|\$\s?\d+(?:\.\d{2})?(?![\d,])/g,
        (fig) => {
            const cents = Math.round(Number(fig.replace(/[$,\s]/g, "")) * 100);
            const note = figures.get(cents);
            return note === undefined ? fig : `<span data-calc-fig="1" data-calc-note="${note.replace(/[&"<>]/g, "")}">${fig}</span>`;
        },
    )).join("");
}

/** A figure in the answer that a calculation produced: dotted underline, and the step behind it on hover. */
export const CalcFigure: React.FC<{ note: string; children?: React.ReactNode }> = ({ note, children }) => {
    const line = useColorModeValue("#c9942b", "#f6c35b");
    return (
        <Tooltip label={<>{note}<br />Computed by code; the worksheet is under the answer.</>} placement="top" hasArrow fontSize="xs">
            <Box as="span" borderBottom="1.5px dotted" borderColor={line} cursor="help">{children}</Box>
        </Tooltip>
    );
};

const DATE_FIELDS = new Set(["date_of_injury", "birth_date", "start", "end"]);

function inputText(k: string, v: any): string {
    if (v === null || v === undefined || v === "") return "—";
    const ymd = DATE_FIELDS.has(k) && typeof v === "string" ? v.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
    if (ymd) {
        return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
            .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    }
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "object") return JSON.stringify(v);
    if (k === "average_weekly_earnings" && !isNaN(Number(v))) return usd(Number(v));
    if (k === "return_to_work") return v === "increase" ? "no offer within 60 days (+15%)" : v === "decrease" ? "offer made (−15%)" : String(v);
    if (k === "rating") return `${v}%`;
    return String(v).replace(/_/g, " ");
}

function worksheetText(calc: CalcResult): string {
    const src = calc.sources_of_inputs ?? {};
    const lines = [`${KIND[calc.kind] ?? calc.kind}: ${calc.headline}`, ""];
    for (const [k, v] of Object.entries(calc.inputs ?? {})) {
        if (k === "impairments" && Array.isArray(v)) {
            v.forEach((im: any) => lines.push(`Impairment ${im.number}: ${im.wpi}% WPI${im.pain_add_on ? ` + ${im.pain_add_on}% pain` : ""}${im.industrial_percent != null ? `, ${im.industrial_percent}% industrial` : ""}`));
        } else {
            lines.push(`${FIELD[k] ?? k.replace(/_/g, " ")}: ${inputText(k, v)}${src[k] ? ` (${src[k]})` : ""}`);
        }
    }
    lines.push("");
    calc.steps.forEach((s, i) => lines.push(`${i + 1}. ${s.label}: ${s.value}${s.rule ? ` [${s.rule}]` : ""}`));
    if (calc.warnings.length) lines.push("", ...calc.warnings.map((w) => `Note: ${w}`));
    if (calc.sources?.length) lines.push("", `Sources: ${calc.sources.join("; ")}`);
    return lines.join("\n");
}

/** Where an input came from, said once for the whole card when it is just the question. */
const FROM_QUESTION = /^the question\.?$/i;
const ASSUMED = /^assumed\b/i;
const MONO = "'SFMono-Regular', 'Menlo', 'Monaco', monospace";
const SERIF = "Bitter, Georgia, serif";

type Palette = Record<"bg" | "border" | "hairline" | "hoverBg" | "accent" | "strong" | "text" | "muted" | "faint" | "amber" | "ink", string>;

function usePalette(): Palette {
    return {
        bg: useColorModeValue("#ffffff", "rgba(26, 32, 44, 0.78)"),
        border: useColorModeValue("rgba(23, 64, 78, 0.14)", "whiteAlpha.200"),
        hairline: useColorModeValue("rgba(23, 64, 78, 0.10)", "rgba(255, 255, 255, 0.10)"),
        hoverBg: useColorModeValue("rgba(23, 64, 78, 0.03)", "whiteAlpha.50"),
        accent: useColorModeValue("#17404e", "#4FD1C5"),
        strong: useColorModeValue("gray.800", "gray.100"),
        text: useColorModeValue("gray.700", "gray.300"),
        muted: useColorModeValue("gray.600", "gray.400"),
        faint: useColorModeValue("gray.500", "gray.500"),
        amber: useColorModeValue("orange.700", "orange.200"),
        ink: useColorModeValue("gray.700", "gray.300"),
    };
}

const SmallCaps: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <Text fontSize="10.5px" fontWeight={600} letterSpacing="0.09em" textTransform="uppercase" color={color}>{children}</Text>
);

function shortDate(v: unknown): string {
    return inputText("date_of_injury", v);
}

function weeksText(w: number): string {
    return Number.isInteger(w) ? `${w}` : w.toFixed(2).replace(/0$/, "");
}

/** The inputs in one line, for the line under a statement's title. */
function contextLine(calc: CalcResult): string {
    return Object.entries(calc.inputs ?? {})
        .filter(([k, v]) => k !== "impairments" && v !== "" && v !== null && v !== undefined)
        .map(([k, v]) => (k === "date_of_injury" ? `Injury ${shortDate(v)}` : `${FIELD[k] ?? k.replace(/_/g, " ")} ${inputText(k, v)}`))
        .join(" · ");
}

/** Assumed or otherwise noted sources of the inputs (the question is said once in the footer). */
function notedSources(calc: CalcResult): { assumed: [string, string][]; other: [string, string][] } {
    const src = calc.sources_of_inputs ?? {};
    const entries = Object.entries(src).filter(([, s]) => s && !FROM_QUESTION.test(s));
    return {
        assumed: entries.filter(([, s]) => ASSUMED.test(s)),
        other: entries.filter(([, s]) => !ASSUMED.test(s)),
    };
}

type Row = { label: string; note?: string; mid?: string; value: string; total?: boolean; section?: boolean; prose?: boolean };

/** An accountant's schedule: what each line is (and the rule), how it was reached, and the figure, in one column. */
const Statement: React.FC<{ rows: Row[]; p: Palette }> = ({ rows, p }) => (
    <Box as="table" w="100%" mt={3} fontSize="13.5px" style={{ borderCollapse: "collapse" }}>
        <tbody>
            {rows.map((r, i) => r.section ? (
                <tr key={i}>
                    <Box as="td" colSpan={3} pt={4} pb={1}>
                        {/* Small caps for the heading only: a citation set in capitals is a different citation ((d) is not (D)). */}
                        <Flex gap={2} align="baseline" wrap="wrap">
                            <SmallCaps color={p.muted}>{r.label}</SmallCaps>
                            {r.note && <Text fontSize="11.5px" color={p.faint}>{r.note}</Text>}
                        </Flex>
                    </Box>
                </tr>
            ) : (
                <Box as="tr" key={i} borderTop={i > 0 && !rows[i - 1].section && !r.total ? "1px solid" : undefined} borderColor={p.hairline}>
                    <Box as="td" py={r.total ? 0 : 2} pt={r.total ? 3 : undefined} pr={4} verticalAlign="top">
                        <Text fontFamily={r.total ? SERIF : undefined} fontWeight={r.total ? 700 : 400} fontSize={r.total ? "15px" : undefined} color={p.strong}>{r.label}</Text>
                        {r.note && <Text fontSize="11.5px" lineHeight="1.45" color={p.faint}>{r.note}</Text>}
                    </Box>
                    <Box as="td" py={2} pr={4} textAlign="right" verticalAlign="top" color={p.muted} whiteSpace="nowrap"
                        display={{ base: "none", md: "table-cell" }}>{r.mid}</Box>
                    {r.total ? (
                        <Box as="td" pt={3} pb={1} textAlign="right" verticalAlign="top" whiteSpace="nowrap" fontFamily={SERIF}
                            fontSize="19px" fontWeight={700} color={p.accent} borderTop="1px solid" borderBottom="3px double" borderColor={p.ink}>
                            {r.value}
                        </Box>
                    ) : (
                        <Box as="td" py={2} textAlign="right" verticalAlign="top" color={p.strong} fontWeight={500}>
                            <Text whiteSpace={r.prose ? "normal" : "nowrap"} maxW={r.prose ? "26ch" : undefined} ml="auto">{r.value}</Text>
                            {r.mid && <Text display={{ base: "block", md: "none" }} fontSize="11.5px" fontWeight={400} color={p.muted} whiteSpace="nowrap">{r.mid}</Text>}
                        </Box>
                    )}
                </Box>
            ))}
        </tbody>
    </Box>
);

function moneyRows(calc: CalcResult): Row[] | null {
    const r = calc.result ?? {};
    const inp = calc.inputs ?? {};
    if (typeof r.weeks !== "number" || typeof r.weekly_rate !== "number" || typeof r.total !== "number") return null;
    const step = (re: RegExp) => calc.steps.find((s) => re.test(s.label));
    const weeksStep = step(/^Weeks/), rateStep = step(/^Weekly rate/), rtwStep = step(/^Return to work/), lpStep = step(/^Life pension/);
    const within = rateStep?.value.match(/within (\$[\d,.]+ – \$[\d,.]+)/)?.[1];
    const awe = inp.average_weekly_earnings;
    const rows: Row[] = [
        { label: "Weeks of payment", note: weeksStep?.rule, mid: `${inp.rating}%`, value: `${weeksText(r.weeks)} wks` },
        {
            label: "Weekly rate",
            note: [within && `limits ${within}`, rateStep?.rule].filter(Boolean).join(" · "),
            mid: /^max/i.test(String(awe)) ? "maximum" : `⅔ × ${usd(Number(awe))}`,
            value: usd(r.weekly_rate),
        },
    ];
    const adjusted = typeof r.adjusted_total === "number" && typeof r.paid_at_base_rate === "number" && typeof r.adjusted_weekly_rate === "number";
    rows.push({ label: adjusted ? "Base award" : "Total owed", mid: `${weeksText(r.weeks)} × ${usd(r.weekly_rate)}`, value: usd(r.total), total: !adjusted });
    if (adjusted) {
        const up = inp.return_to_work === "increase";
        const rest = Math.max(0, r.weeks - 60 / 7);
        rows.push(
            { label: "Return-to-work adjustment", note: rtwStep?.rule, value: "", section: true },
            { label: "First 60 days at the base rate", mid: "8 wks 4 days", value: usd(r.paid_at_base_rate) },
            { label: `Remaining weeks at ${up ? "+15%" : "−15%"}`, note: up ? "No offer of work within 60 days of P&S" : "Offer of work within 60 days of P&S",
              mid: `${rest.toFixed(2)} × ${usd(r.adjusted_weekly_rate)}`, value: usd(r.adjusted_total - r.paid_at_base_rate) },
            { label: "Total owed", value: usd(r.adjusted_total), total: true },
        );
    } else if (typeof r.adjusted_weekly_rate === "number") {
        rows.splice(rows.length - 1, 0, { label: "Adjusted weekly rate", note: rtwStep?.rule, mid: rtwStep?.value.split(" a week")[0], value: usd(r.adjusted_weekly_rate) });
    }
    if (typeof r.life_pension_weekly === "number") {
        rows.push({ label: "Life pension, after the PD weeks", note: [lpStep?.value.split(" = ")[0], lpStep?.rule].filter(Boolean).join(" · "),
                    value: `${usd(r.life_pension_weekly)} a week` });
    }
    return rows;
}

function genericRows(calc: CalcResult): Row[] {
    return [
        ...calc.steps.map((s) => ({ label: s.label, note: s.rule, value: s.value, prose: true })),
        { label: "Result", value: calc.headline, total: true },
    ];
}

/** The rating string set large, each part explained beneath it, its rules as footnotes. */
const RatingFormula: React.FC<{ calc: CalcResult; p: Palette }> = ({ calc, p }) => {
    const r = calc.result ?? {};
    const inp = calc.inputs ?? {};
    const src = calc.sources_of_inputs ?? {};
    const imps: any[] = Array.isArray(r.impairments) ? r.impairments : [];
    const notes: string[] = [];
    const fn = (text?: string) => {
        if (!text) return undefined;
        let i = notes.indexOf(text);
        if (i < 0) { notes.push(text); i = notes.length - 1; }
        return i + 1;
    };
    const ageRule = calc.steps.find((s) => /^Age band/.test(s.label))?.rule;
    const impSource = src.impairments && !FROM_QUESTION.test(src.impairments) ? src.impairments : undefined;
    const many = imps.length > 1;
    const combos = calc.steps.filter((s) => /combined$|^Combined$|^Added$/i.test(s.label) || /: combined$/.test(s.label));

    const formulas = imps.map((imp, k) => {
        const step = calc.steps.find((s) => s.value.startsWith(`${imp.number} –`));
        const [adjRule, variantRule] = (step?.rule ?? "").split("; ");
        const tag = String(imp.string ?? "").match(/\[([^\]]+)\]/)?.[1] ?? "";
        const whole = (imp.wpi ?? 0) + (imp.pain_add_on ?? 0);
        const finalIsAge = imp.industrial_percent == null;
        const segs: { main: string; sub: string; mono?: boolean; n?: number; accent?: boolean }[] = [
            { main: imp.number, sub: imp.label || imp.description || "impairment", mono: true, n: fn(impSource) },
            { main: `${Number(whole.toFixed(2))}%`, sub: imp.pain_add_on ? `${imp.wpi} + ${imp.pain_add_on} pain` : "WPI" },
            { main: `[${tag}] ${imp.adjusted}`, sub: tag === "1.4" ? "× 1.4" : `FEC rank ${tag}`, n: fn(adjRule) },
            { main: `${r.occupational_group ?? inp.occupational_group}${imp.variant}`, sub: `variant ${imp.variant}`, n: fn(variantRule?.replace(/^variant \w from /, "")) },
            { main: `${imp.occupational}`, sub: "occupation", n: fn("PDRS section 5: occupational adjustment") },
            { main: `${imp.age_adjusted}%`, sub: `age ${r.age ?? inp.age}`, n: fn(ageRule), accent: !many && finalIsAge },
        ];
        if (!finalIsAge) segs.push({ main: `${imp.final}%`, sub: `× ${imp.industrial_percent}% industrial`, accent: !many });
        return (
            <Flex key={k} wrap="wrap" columnGap={5} rowGap={4} mt={k ? 4 : 3} align="baseline">
                {segs.map((s, i) => (
                    <Box key={i} minW={0} maxW="150px">
                        <Text fontFamily={MONO} fontSize={s.accent ? "26px" : "19px"} lineHeight="1.2" fontWeight={s.accent ? 600 : 400}
                            color={s.accent ? p.accent : p.strong} whiteSpace="nowrap">{s.main}</Text>
                        <Box mt={1.5} pt={1} borderTop="1px solid" borderColor={s.accent ? p.accent : p.border}>
                            <Text fontSize="11.5px" lineHeight="1.35" color={s.accent ? p.accent : p.muted}>
                                {s.sub}{s.n && <Text as="sup" ml="1px" fontSize="9.5px" color={p.accent}>{s.n}</Text>}
                            </Text>
                        </Box>
                    </Box>
                ))}
            </Flex>
        );
    });

    const occSource = src.occupational_group && !FROM_QUESTION.test(src.occupational_group) ? src.occupational_group : undefined;
    const methodAssumed = src.method && ASSUMED.test(src.method);
    const methodText = inp.method === "add" ? "the ratings are added" : "ratings are combined by the combined-values method";

    return (
        <>
            {formulas}
            {combos.map((s, i) => (
                <Flex key={i} mt={4} gap={3} align="baseline" wrap="wrap">
                    <Text fontSize="12px" color={p.muted}>{s.label}</Text>
                    <Text fontFamily={MONO} fontSize="15px" color={p.strong}>{s.value}</Text>
                    {s.rule && <Text as="sup" fontSize="9.5px" color={p.accent}>{fn(s.rule)}</Text>}
                </Flex>
            ))}
            {many && (
                <Text mt={3} fontFamily={SERIF} fontSize="22px" fontWeight={700} color={p.accent}>{calc.headline}</Text>
            )}
            <Text mt={4} fontFamily={SERIF} fontSize="14.5px" lineHeight="1.65" color={p.text}>
                Injured {shortDate(inp.date_of_injury ?? inp.birth_date)} at {r.age ?? inp.age}, occupational group {r.occupational_group ?? inp.occupational_group}
                {occSource && <Text as="sup" ml="1px" fontSize="9.5px" color={p.accent}>{fn(occSource)}</Text>}
                {(many || inp.method) && <>; {methodText}{methodAssumed && <Text as="sup" ml="1px" fontSize="10px" color={p.amber}>*</Text>}</>}.
                {r.schedule && <Text as="span" color={p.muted}> {String(r.schedule).replace(/\s*\(.*\)$/, "")}.</Text>}
            </Text>
            {notes.length > 0 && (
                <Box as="ol" mt={3} pt={2.5} pl={5} borderTop="1px solid" borderColor={p.hairline} fontSize="11.5px" lineHeight="1.55" color={p.muted}>
                    {notes.map((n, i) => <li key={i}>{n}</li>)}
                </Box>
            )}
            {methodAssumed && (
                <Text mt={1.5} fontSize="11.5px" color={p.amber}>* {src.method}</Text>
            )}
        </>
    );
};

const CalcCard: React.FC<{ calc: CalcResult; collapsible: boolean }> = ({ calc, collapsible }) => {
    const [open, setOpen] = useState(!collapsible);
    const { onCopy, hasCopied } = useClipboard(worksheetText(calc));
    const p = usePalette();
    const title = KIND[calc.kind] ?? calc.kind;
    const isRating = calc.kind === "pd_rating" && Array.isArray(calc.result?.impairments) && calc.result!.impairments.length > 0;
    const rows = calc.kind === "pd_money" ? moneyRows(calc) ?? genericRows(calc) : genericRows(calc);
    const fromQuestion = Object.values(calc.sources_of_inputs ?? {}).some((s) => FROM_QUESTION.test(s));
    const { assumed, other } = notedSources(calc);

    return (
        <Box as="section" aria-label={title} border="1px solid" borderColor={p.border} bg={p.bg} borderRadius="12px"
            color={p.text} fontSize="13.5px" lineHeight="1.5" sx={{ fontVariantNumeric: "tabular-nums lining-nums" }}>
            <Box as={collapsible ? "button" : "div"} display="block" w="100%" textAlign="left" px={6} pt={5} pb={open ? 0 : 5}
                onClick={collapsible ? () => setOpen(!open) : undefined} aria-expanded={collapsible ? open : undefined}
                cursor={collapsible ? "pointer" : "default"} _hover={collapsible ? { bg: p.hoverBg } : undefined}
                borderTopRadius="12px" borderBottomRadius={open ? 0 : "12px"} transition="background 0.15s ease">
                <Flex align="baseline" gap={3}>
                    <SmallCaps color={p.muted}>{title}</SmallCaps>
                    {open && (
                        <Button as="span" role="button" size="xs" variant="ghost" ml="auto" color={p.faint} fontWeight={500} h="22px" px={1.5}
                            leftIcon={hasCopied ? <FaCheck size={10} /> : <FaRegCopy size={10} />}
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onCopy(); }}>
                            {hasCopied ? "Copied" : "Copy"}
                        </Button>
                    )}
                    {collapsible && (
                        <Box as={FaChevronDown} ml={open ? 0 : "auto"} color={p.faint} fontSize="10px" flexShrink={0} aria-hidden
                            transform={open ? "rotate(180deg)" : "none"} transition="transform 0.2s ease" alignSelf="center" />
                    )}
                </Flex>
                {!open && (
                    <Text mt={1} fontFamily={SERIF} fontSize="17px" fontWeight={700} lineHeight="1.35" color={p.accent}>{calc.headline}</Text>
                )}
                {open && !isRating && (
                    <Text mt={1} fontSize="12px" color={p.faint}>{contextLine(calc)}</Text>
                )}
            </Box>

            {open && (
                <Box px={6} pb={5}>
                    {isRating ? <RatingFormula calc={calc} p={p} /> : <Statement rows={rows} p={p} />}

                    {!isRating && other.length > 0 && (
                        <Box mt={3} fontSize="11.5px" lineHeight="1.5" color={p.muted}>
                            {other.map(([k, s]) => <Text key={k}>{FIELD[k] ?? k.replace(/_/g, " ")}: {s}</Text>)}
                        </Box>
                    )}

                    {(calc.warnings.length > 0 || (!isRating && assumed.length > 0)) && (
                        <Box mt={4} fontSize="12px" lineHeight="1.5" color={p.amber}>
                            {!isRating && assumed.map(([k, s]) => (
                                <Flex key={k} gap={2} align="baseline"><Box as={FaExclamationTriangle} fontSize="9.5px" flexShrink={0} aria-hidden /><Text>{FIELD[k] ?? k.replace(/_/g, " ")}: {s}</Text></Flex>
                            ))}
                            {calc.warnings.map((w, i) => (
                                <Flex key={i} gap={2} align="baseline" mt={1}><Box as={FaExclamationTriangle} fontSize="9.5px" flexShrink={0} aria-hidden /><Text>{w}</Text></Flex>
                            ))}
                        </Box>
                    )}

                    <Text mt={4} fontSize="11px" lineHeight="1.5" color={p.faint}>
                        {fromQuestion && "Inputs from the question unless noted · "}
                        {!isRating && calc.sources?.length ? `${calc.sources.join(" · ")} · ` : ""}
                        computed by code, not by the model
                    </Text>
                </Box>
            )}
        </Box>
    );
};

const CalcCards: React.FC<{ calcs: CalcResult[] }> = ({ calcs }) => {
    if (calcs.length === 0) return null;
    // One calculation opens as a full worksheet; several start folded to their results, so the chat stays readable.
    return (
        <Flex direction="column" gap={2} mt={4}>
            {calcs.map((c, i) => <CalcCard key={i} calc={c} collapsible={calcs.length > 1} />)}
        </Flex>
    );
};

export default CalcCards;
